import {
  ContextChangesetBase,
  OrgGuideFileVersionId,
  SemanticProjectChangeStatus,
  WORKSPACE_CONTEXT_FILE_PATH,
  typedObjectFromEntries,
} from "@hex/common";
import { keyBy } from "lodash";

import { ORG_ID } from "../../../../orgs";
import { selectContextWorkbench } from "../../../../redux/slices/contextWorkbenchSlice";
import { RootState } from "../../../../redux/store";
import { createSelectorV2 } from "../../../../redux/utils/index.js";
import { WorkbenchDiffStatus } from "../../../workbench/state/diffStatus";
import { PublishContextChangesetFragment } from "../../queries/useInitPublishContextChangeset.generated";
import { OrgGuideFileWithBaseVersionFragment } from "../../queries/useInitPublishContextChangesetBaseFiles.generated";
import { ContextWorkbenchEntity } from "../contextWorkbenchEntity";
import { ContextWorkbenchFileId } from "../contextWorkbenchFile";
import { ContextWorkbenchPublish } from "../contextWorkbenchPublish";
import { ContextWorkbenchUnifiedPreview } from "../contextWorkbenchUnifiedPreview";
import {
  getEditorFilesListNodeForAsyncPlaceholder,
  getEditorFilesListNodeForFile,
} from "../utils/editorFilesListNode";
import { fileComparator, getFileForEntity } from "../utils/file";
import {
  buildOrgGuideFileForChange,
  buildOrgGuideFileGroup,
  buildSemanticProjectGroups,
  countEditedFiles,
} from "../utils/publishFile";
import {
  getContextWorkbenchFileNodeGroups,
  getContextWorkbenchFileNodeGroupsAsyncPlaceholder,
} from "../utils/sources";

import { ContextWorkbenchSelectorOptions as SelectorOptions } from "./options";

type SliceState = Pick<RootState, "contextWorkbench">;

const selectPublishFiles = createSelectorV2(
  [
    (s: SliceState) => selectContextWorkbench(s).publish.changeset,
    (s: SliceState) => selectContextWorkbench(s).publish.baseFiles,
  ],
  (changeset, baseFiles) => {
    if (!changeset.value || !baseFiles.value) {
      return [];
    }
    const orgGuideFileChangesByFileId = keyBy(
      changeset.value.orgGuideFileChanges,
      (
        change: PublishContextChangesetFragment["orgGuideFileChanges"][number],
      ) => change.orgGuideFile.id,
    );
    const changesetBase = changeset.value.base;
    const orgId = changeset.value.orgId;
    const getBaseVersionForFile = (
      orgGuideFile: OrgGuideFileWithBaseVersionFragment,
    ):
      | OrgGuideFileWithBaseVersionFragment["latestVersion"]
      | OrgGuideFileWithBaseVersionFragment["draftVersion"] => {
      if (changesetBase === ContextChangesetBase.DRAFT) {
        return orgGuideFile.draftVersion;
      } else {
        return orgGuideFile.latestVersion;
      }
    };

    const files = baseFiles.value
      .map((baseFile) => {
        const maybeChange = orgGuideFileChangesByFileId[baseFile.id];
        if (!maybeChange) {
          const baseVersion = getBaseVersionForFile(baseFile);
          const file = getFileForEntity({
            type: ContextWorkbenchEntity.Type.OrgGuideFile,
            contents: baseVersion?.contentsText ?? null,
            filePath: baseFile.filePath,
            orgId: ORG_ID,
            orgGuideFileId: baseFile.id,
            orgGuideFileVersionId:
              baseVersion?.id ??
              OrgGuideFileVersionId.check(`#deleted:${baseFile.id}`),
            externalSource: baseVersion?.externalSource ?? null,
          });
          return {
            ...file,
            diff: {
              status: WorkbenchDiffStatus.UNMODIFIED,
              baseContents: baseVersion?.contentsText ?? null,
            },
            externalSource: baseVersion?.externalSource ?? null,
          };
        } else {
          return buildOrgGuideFileForChange(maybeChange, orgId);
        }
      })
      .filter(
        // baseFiles also includes deleted files meaning we also need to filter out files that are deleted that have no diff
        (changes) =>
          !(
            changes.diff.status === WorkbenchDiffStatus.UNMODIFIED &&
            changes.contents == null
          ),
      )
      .sort(fileComparator);
    return files;
  },
);

const selectVisibleFiles = createSelectorV2(
  [
    selectPublishFiles,
    (s: SliceState) => selectContextWorkbench(s).publish.changesFilter,
    (_s: SliceState, options: SelectorOptions) => options,
  ],
  (files, changesFilter, options) => {
    return files
      .filter((file) => {
        if (!options.replaceWorkspaceRulesEnabled) {
          return file.filepath !== WORKSPACE_CONTEXT_FILE_PATH;
        }
        return true;
      })
      .filter((file) =>
        changesFilter === ContextWorkbenchPublish.ChangesFilter.CHANGED
          ? file.diff.status !== WorkbenchDiffStatus.UNMODIFIED
          : true,
      );
  },
);

const selectPublishFilesById = createSelectorV2(
  [selectPublishFiles],
  (files) => {
    return typedObjectFromEntries(files.map((file) => [file.id, file]));
  },
);

const selectSelectedPublishFile = createSelectorV2(
  [
    selectPublishFilesById,
    (s: SliceState) => selectContextWorkbench(s).publish.selectedFileId,
  ],
  (filesById, selectedFileId) => {
    return selectedFileId ? (filesById[selectedFileId] ?? null) : null;
  },
);

const selectPublishFileListNodesBySource = createSelectorV2(
  [
    (s: SliceState) => selectContextWorkbench(s).publish.changeset,
    selectVisibleFiles,
  ],
  (changeset, visibleFiles) => {
    if (changeset.loading || changeset.error) {
      const node = getEditorFilesListNodeForAsyncPlaceholder(
        ContextWorkbenchEntity.Type.OrgGuideFile,
        changeset.loading,
        changeset.error,
      );
      return getContextWorkbenchFileNodeGroupsAsyncPlaceholder(node);
    } else {
      const nodes = visibleFiles.map((file) => ({
        ...getEditorFilesListNodeForFile(file.id),
        externalSource: file.externalSource,
      }));

      return getContextWorkbenchFileNodeGroups(nodes);
    }
  },
);

const selectFirstVisibleFile = createSelectorV2(
  [selectVisibleFiles],
  (files) => {
    return (
      files.find((f) => f.diff.status !== WorkbenchDiffStatus.UNMODIFIED)?.id ??
      files[0]?.id ??
      null
    );
  },
);

const selectPublishFormData = (s: SliceState) =>
  selectContextWorkbench(s).publish.formData;

const selectUnifiedPreviewResources = createSelectorV2(
  [(s: SliceState) => selectContextWorkbench(s).publish.changeset.value],
  (changeset): ContextWorkbenchUnifiedPreview.Resources => {
    if (changeset == null) {
      return { groups: [], editedFileCount: 0 };
    }

    const orgGuideGroup = buildOrgGuideFileGroup(changeset);
    const groups: ContextWorkbenchUnifiedPreview.ResourceGroup[] = [
      ...(orgGuideGroup ? [orgGuideGroup] : []),
      ...buildSemanticProjectGroups(changeset),
    ];

    return { groups, editedFileCount: countEditedFiles(groups) };
  },
);

const selectUnifiedPreviewVisibleGroups = createSelectorV2(
  [
    selectUnifiedPreviewResources,
    (s: SliceState) => selectContextWorkbench(s).publish.changesFilter,
    (_s: SliceState, options: SelectorOptions) => options,
  ],
  (
    { groups },
    changesFilter,
    options,
  ): ContextWorkbenchUnifiedPreview.ResourceGroup[] => {
    const showChangedOnly =
      changesFilter === ContextWorkbenchPublish.ChangesFilter.CHANGED;
    return groups
      .map((group): ContextWorkbenchUnifiedPreview.ResourceGroup => {
        if (
          group.type ===
          ContextWorkbenchUnifiedPreview.ResourceGroup.Type.SemanticProject
        ) {
          return group;
        }
        const files = group.files
          .filter((file) =>
            options.replaceWorkspaceRulesEnabled
              ? true
              : file.filepath !== WORKSPACE_CONTEXT_FILE_PATH,
          )
          .filter((file) =>
            showChangedOnly
              ? file.diff.status !== WorkbenchDiffStatus.UNMODIFIED
              : true,
          );
        return { ...group, files };
      })
      .filter((group) => {
        if (
          group.type ===
          ContextWorkbenchUnifiedPreview.ResourceGroup.Type.SemanticProject
        ) {
          return showChangedOnly
            ? group.status === SemanticProjectChangeStatus.MODIFIED
            : true;
        }
        return group.files.length > 0;
      });
  },
);

export const contextWorkbenchPublishSelectors = {
  selectIsPublishing: (s: SliceState) =>
    selectContextWorkbench(s).view === "publish",
  selectView: (s: SliceState) => selectContextWorkbench(s).publish.view,
  selectBaseFiles: (s: SliceState) =>
    selectContextWorkbench(s).publish.baseFiles,
  selectChangeset: (s: SliceState) =>
    selectContextWorkbench(s).publish.changeset,
  selectLoadedChangeset: (s: SliceState) =>
    contextWorkbenchPublishSelectors.selectChangeset(s).value,
  selectAskPreviewThread: (s: SliceState) =>
    selectContextWorkbench(s).publish.askPreviewThread,
  selectFiles: selectPublishFiles,
  selectVisibleFiles: selectVisibleFiles,
  selectFilesById: selectPublishFilesById,
  selectFileById: (fileId: ContextWorkbenchFileId) => (s: SliceState) =>
    selectPublishFilesById(s)[fileId],
  selectFirstVisibleFile: selectFirstVisibleFile,
  selectSelectedFile: selectSelectedPublishFile,
  selectFileListNodesBySource: selectPublishFileListNodesBySource,
  selectFormData: selectPublishFormData,
  selectUnifiedPreviewResources: selectUnifiedPreviewResources,
  selectUnifiedPreviewVisibleGroups: selectUnifiedPreviewVisibleGroups,
};
